// S9 — Resumen hora a hora por línea (supervisor), por TURNO.
//
// Pivote tipo Excel del cliente: filas = franjas horarias, columnas = líneas del
// scope + una columna TIENDAS (subsistema ortogonal, ubicado por hora de inicio
// de cada corrida). Cada supervisor ve SOLO sus plantas (scope estructural) y
// elige el turno (default: su turno programado hoy).
//
// La PANTALLA muestra todo (validado + pendiente) para que se vea qué falta
// validar. El botón "Descargar foto" genera un PNG con SOLO lo validado —
// incentivo directo: lo que no validás, no sale en la foto que mandás al grupo.
//
// Mobile + PC: la tabla es ancha, así que va con scroll horizontal; la primera
// columna (franja) queda sticky. La columna TIENDAS se resalta.

import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { semaforoCumplimiento, toneTextClass } from '../../lib/semaforo';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import type { SupervisorResumenTurno } from 'schemas';
import { fetchResumenHoraValidado, useSupervisorResumenHora } from '../../lib/api/queries';
import { descargarResumenImagen } from './resumenImagen';

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined || n === 0
    ? '—'
    : n.toLocaleString('es-CO', { maximumFractionDigits: 0 });

function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Estado del turno para el badge de la foto. Aproximación con reloj local:
 * fecha pasada → cerrado; hoy → compara la hora actual contra el rango del
 * turno (si cruza medianoche, "en curso" fuera del hueco fin→inicio).
 */
function estadoTurno(
  fechaISO: string,
  turno: SupervisorResumenTurno | undefined,
): 'cerrado' | 'en_curso' | null {
  if (!turno) return null;
  const hoy = hoyISO();
  if (fechaISO < hoy) return 'cerrado';
  if (fechaISO > hoy) return null;
  const d = new Date();
  const ahora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const cruza = turno.hora_fin <= turno.hora_inicio;
  if (!cruza) {
    if (ahora >= turno.hora_fin) return 'cerrado';
    if (ahora >= turno.hora_inicio) return 'en_curso';
    return null;
  }
  return ahora >= turno.hora_inicio || ahora < turno.hora_fin ? 'en_curso' : 'cerrado';
}

const ROL_LABELS: Record<string, string> = {
  supervisor: 'Supervisor',
  super_admin: 'Super Admin',
};

export function S9ResumenHoraPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  // Deep-link desde el CTA de cierre de turno (S1): ?fecha=YYYY-MM-DD&turno=N.
  // Clave para T3 (cruza medianoche): su fecha operativa es la de AYER.
  const [searchParams] = useSearchParams();
  const fechaParam = searchParams.get('fecha');
  const turnoParam = Number(searchParams.get('turno'));
  const [fecha, setFecha] = useState(
    fechaParam && /^\d{4}-\d{2}-\d{2}$/.test(fechaParam) ? fechaParam : hoyISO(),
  );
  // null → el backend resuelve el turno programado; número → selección explícita.
  const [turnoSel, setTurnoSel] = useState<number | null>(
    Number.isInteger(turnoParam) && turnoParam >= 1 && turnoParam <= 9 ? turnoParam : null,
  );
  const [descargando, setDescargando] = useState(false);
  const [confirmarDescarga, setConfirmarDescarga] = useState(false);
  const { data, isLoading, isError, error, isFetching } = useSupervisorResumenHora(fecha, turnoSel);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  const columnas = data?.columnas ?? [];
  const filas = data?.filas ?? [];
  const turnos = data?.turnos ?? [];
  const turnoActivo = turnoSel ?? data?.turno_numero ?? null;
  const hayDatos = filas.length > 0 && columnas.length > 0;

  // Cumplimiento del turno vs meta (misma cuenta que la card de la foto).
  const meta = data?.meta_total && data.meta_total > 0 ? data.meta_total : null;
  const cumplimientoPct = meta ? Math.round((data!.total_general / meta) * 100) : null;

  // Una celda ámbar lleva a la línea donde se valida (las tiendas SON líneas).
  const irAValidar = (col: { key: string; es_tienda: boolean }) =>
    navigate(`/supervisor/linea/${col.key}`);

  // Total sin validar del turno en pantalla — si hay, se confirma antes de
  // descargar: la foto va a salir SIN esa producción.
  const totalPendiente = columnas.reduce((acc, c) => acc + c.pendiente, 0);

  const onDescargar = () => {
    if (descargando) return;
    if (totalPendiente > 0) {
      setConfirmarDescarga(true);
      return;
    }
    void descargarFoto();
  };

  const descargarFoto = async () => {
    if (descargando) return;
    setConfirmarDescarga(false);
    setDescargando(true);
    try {
      const validado = await fetchResumenHoraValidado(fecha, turnoActivo);
      if (validado.filas.length === 0) {
        toast.error(
          'No hay información validada en este turno todavía. Valide las corridas y capturas para poder compartir la foto.',
        );
        return;
      }
      const turnoMeta = validado.turnos.find((t) => t.numero === validado.turno_numero);
      await descargarResumenImagen(validado, turnoMeta, {
        theme: document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light',
        rolLabel: ROL_LABELS[state.claims.rol] ?? 'Supervisor',
        estadoTurno: estadoTurno(validado.fecha, turnoMeta),
      });
      toast.success('Foto generada');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No pudimos generar la foto');
    } finally {
      setDescargando(false);
    }
  };

  return (
    <RoleLayout sidebar={<SupervisorSidebar />} roleLabel="Supervisor">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Resumen hora a hora</h1>
            <p className="text-sm text-fg-tertiary">
              Cantidad producida por línea y franja horaria. Incluye Tiendas.
              {data?.plantas?.length ? ` · ${data.plantas.join(' · ')}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="s9-fecha">Fecha</Label>
              <Input
                id="s9-fecha"
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-40"
              />
            </div>
            <Button
              variant="primary"
              onClick={onDescargar}
              loading={descargando}
              disabled={!hayDatos}
            >
              Descargar foto
            </Button>
            {isFetching ? <Spinner className="mb-2 size-4" /> : null}
          </div>
        </header>

        {turnos.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-fg-tertiary">Turno:</span>
            {turnos.map((t) => (
              <Button
                key={t.numero}
                size="sm"
                variant={turnoActivo === t.numero ? 'primary' : 'outline'}
                onClick={() => setTurnoSel(t.numero)}
              >
                {t.nombre} · {t.hora_inicio}–{t.hora_fin}
              </Button>
            ))}
          </div>
        ) : null}

        {hayDatos ? (
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 rounded-md border border-border bg-surface-elevated px-4 py-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-fg-tertiary">Producido</p>
              <p className="text-lg font-semibold tabular-nums text-fg">
                {fmt(data?.total_general)}
              </p>
            </div>
            {meta ? (
              <>
                <div>
                  <p className="text-xs uppercase tracking-wide text-fg-tertiary">Meta del turno</p>
                  <p className="text-lg font-semibold tabular-nums text-fg">{fmt(meta)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-fg-tertiary">Cumplimiento</p>
                  <p
                    className={cn(
                      'text-lg font-semibold tabular-nums',
                      toneTextClass(semaforoCumplimiento(cumplimientoPct)),
                    )}
                  >
                    {cumplimientoPct}%
                  </p>
                </div>
              </>
            ) : (
              <p className="text-xs text-fg-tertiary">
                Sin meta configurada para este turno (capacidades estándar).
              </p>
            )}
          </div>
        ) : null}

        <p className="flex flex-wrap items-center gap-x-1 text-xs text-fg-tertiary">
          <span>La foto para compartir muestra</span>
          <strong>solo lo validado</strong>
          <span>. Lo marcado en</span>
          <span className="inline-flex items-center gap-1 rounded bg-warn-soft px-1.5 py-0.5 font-medium text-warn">
            ● ámbar
          </span>
          <span>está sin validar y NO sale en la imagen — toque la celda para ir a validar.</span>
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-fg-tertiary">
            <Spinner className="size-5" /> Cargando resumen…
          </div>
        ) : isError ? (
          <div className="rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
            No pudimos cargar el resumen. {error instanceof Error ? error.message : ''}
          </div>
        ) : !hayDatos ? (
          <div className="rounded-md border border-border bg-surface-elevated px-4 py-8 text-center text-sm text-fg-tertiary">
            No hay producción registrada para este turno en sus plantas.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full border-collapse text-right text-sm tabular-nums">
              <thead>
                <tr className="bg-surface-elevated text-fg-secondary">
                  <th className="sticky left-0 z-10 bg-surface-elevated px-3 py-2 text-left font-medium">
                    Hora a Hora
                  </th>
                  {columnas.map((c) => (
                    <th key={c.key} className="whitespace-nowrap px-3 py-2 font-medium">
                      {c.nombre}
                      {c.pendiente > 0 ? (
                        <span
                          aria-hidden
                          className="ml-1 cursor-pointer text-warn"
                          title="Tiene bloques sin validar — click para ir a validar"
                          onClick={() => irAValidar(c)}
                        >
                          ●
                        </span>
                      ) : null}
                    </th>
                  ))}
                  <th className="whitespace-nowrap border-l border-border px-3 py-2 font-semibold text-fg">
                    Suma total
                  </th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f.franja_inicio} className="border-t border-border">
                    <td className="sticky left-0 z-10 bg-surface-base px-3 py-1.5 text-left font-medium text-fg-secondary">
                      {f.franja}
                    </td>
                    {columnas.map((c) => {
                      const pend = f.pendientes[c.key] ?? 0;
                      return (
                        <td
                          key={c.key}
                          className={cn(
                            'px-3 py-1.5 text-fg',
                            pend > 0 &&
                              'cursor-pointer bg-warn-soft font-medium text-warn underline decoration-dotted underline-offset-4 hover:bg-warn-soft/80',
                          )}
                          title={
                            pend > 0
                              ? `Sin validar: ${pend.toLocaleString('es-CO')} — no sale en la foto. Click para ir a validar.`
                              : undefined
                          }
                          onClick={pend > 0 ? () => irAValidar(c) : undefined}
                        >
                          {fmt(f.valores[c.key])}
                          {pend > 0 ? (
                            <span aria-hidden className="ml-1">
                              ●
                            </span>
                          ) : null}
                        </td>
                      );
                    })}
                    <td className="border-l border-border px-3 py-1.5 font-semibold text-fg">
                      {fmt(f.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-surface-elevated font-semibold text-fg">
                  <td className="sticky left-0 z-10 bg-surface-elevated px-3 py-2 text-left">
                    Suma total
                  </td>
                  {columnas.map((c) => (
                    <td key={c.key} className="px-3 py-2">
                      {fmt(c.total)}
                    </td>
                  ))}
                  <td className="border-l border-border px-3 py-2">{fmt(data?.total_general)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Confirmación: hay producción sin validar que NO va a salir en la foto */}
      <Dialog open={confirmarDescarga} onClose={() => setConfirmarDescarga(false)}>
        <DialogHeader>
          <DialogTitle>Falta información por validar</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <p className="text-sm text-fg-secondary">
            Hay{' '}
            <strong className="text-warn">
              {totalPendiente.toLocaleString('es-CO')} unidades sin validar
            </strong>{' '}
            en este turno. La foto solo incluye lo validado, así que esa producción{' '}
            <strong>no va a aparecer</strong> en la imagen que comparta.
          </p>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmarDescarga(false)}>
            Cancelar y validar primero
          </Button>
          <Button variant="primary" onClick={() => void descargarFoto()}>
            Descargar igual
          </Button>
        </DialogFooter>
      </Dialog>
    </RoleLayout>
  );
}
